import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field } from 'react-final-form';
import Select from 'react-select';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { withViewport } from '../../../util/uiHelpers';
import getCountryCodes from '../../../translations/countryCodes';
import { FormattedMessage } from '../../../util/reactIntl';
import { manageDisableScrolling } from '../../../ducks/ui.duck';

import { PrimaryButton } from '../../../components';

import ProfileAddress from '../../ProfileAddress/ProfileAddress';
import Modal from '../../Modal/Modal';

import css from './BookingDatesForm.module.css';

const FieldDeliveryAddressComponent = ({ input, onChange, focusedSelect, viewport, userType }) => {
  const intl = useIntl();
  const isMobileLayout = viewport.width < 768;
  const currentUser = useSelector(state => state.user.currentUser);
  const dispatch = useDispatch();
  const [isOpenAddAddressModal, setIsOpenAddAddressModal] = useState(null);
  const [newValueToSelect, setNewValueToSelect] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const selectRef = useRef();

  const addressFieldIndex = addresses.length - 1;
  const options = addresses.map((address, index) => {
    const addressId = address.id;
    const name = address.name;
    const street = address.street;
    const streetNumber = address.streetNumber;
    const postalcode = address.postalcode;
    const city = address.city;
    const country = getCountryCodes(intl.locale).find(item => item.code === address.country)?.name;
    const countryCode = address.country;
    const gpsCoordinates = address.gpsCoordinates; // eslint-disable-line no-unused-vars
    const companyName = address.companyName;
    const contactPersonOnSite = address.contactPersonOnSite;
    const contactPhoneOnSite = address.contactPhoneOnSite;
    const additionalInfo = address.additionalInfo;

    const label = (
      <>
        <span className={css.deliveryAddressOptionTitle}>{name}</span>
        <br />
        <FormattedMessage
          id="BookingDatesForm.deliveryAddressOption"
          values={{
            id: addressId,
            street,
            streetNumber,
            postalcode,
            city,
            country,
            name,
            gpsCoordinates,
            countryCode,
            companyName,
            contactPersonOnSite,
            contactPhoneOnSite,
            additionalInfo,
          }}
        />
      </>
    );

    return {
      value: {
        id: addressId,
        street,
        streetNumber,
        postalcode,
        city,
        country,
        gpsCoordinates,
        name,
        countryCode,
        companyName,
        contactPersonOnSite,
        contactPhoneOnSite,
        additionalInfo,
      },
      label,
    };
  });

  const onManageDisableScrolling = (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling));

  const handleAddAddressSuccess = () => {
    setIsOpenAddAddressModal(false);
    setNewValueToSelect(true);
  };

  const handleOpenProfileAddressModal = () => {
    const newIndex = addresses.length;
    setAddresses([...addresses, { id: uuidv4() }]);
    setIsOpenAddAddressModal(newIndex);
  };

  const handleCloseProfileAddressModal = () => {
    if (!addresses[addresses.length - 1]?.name) {
      setAddresses(addresses.slice(0, addresses.length - 1));
    }
    setIsOpenAddAddressModal(null);
  };

  useEffect(() => {
    if (focusedSelect) {
      selectRef.current.focus();
    }
  }, [focusedSelect]);

  useEffect(() => {
    setAddresses(currentUser?.attributes?.profile?.protectedData?.addresses || []);
  }, [currentUser?.attributes?.profile?.protectedData?.addresses]);

  useEffect(() => {
    if (newValueToSelect && options.length > 0) {
      input.onChange(options[options.length - 1]);
      setNewValueToSelect(false);
    }
  }, [options.length, newValueToSelect]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className={css.deliveryAddresses}>
        {addresses && addresses.length ? (<Select
          {...input}
          className={css.deliverySelect}
          styles={{
            valueContainer: styles => ({
              ...styles,
              height: isMobileLayout ? 38 : 52,
            }),
            control: provided => ({
              ...provided,
              borderRadius: '6px',
              border: '1px solid #d8dce6',
            }),
            indicatorSeparator: provided => ({ ...provided, display: 'none' }),
          }}
          options={options}
          isSearchable={false}
          openMenuOnFocus={true}
          ref={selectRef}
          placeholder={intl.formatMessage({ id: 'BookingTimeForm.selectPlaceHolder' })}
          noOptionsMessage={() => intl.formatMessage({ id: 'BookingTimeForm.selectNoOptionText' })}
        />) : (<></>)}
        {userType !== 'lessor' && (addresses && addresses.length ?
          (<div className={css.addMoreAddress} onClick={handleOpenProfileAddressModal}>
            <FormattedMessage id="BookingDatesForm.DeliveryAddresses.addMoreAddress" />
            </div>) :
            (<PrimaryButton onClick={handleOpenProfileAddressModal}>
              <FormattedMessage id="BookingDatesForm.DeliveryAddresses.addMoreAddress" />
              </PrimaryButton>)
            )}
      </div>
      <Modal
        id={`BookingDatesForm.profileAddressForm.`}
        contentClassName={css.modalContent}
        extraContainerClassName={css.addressModalContainer}
        isOpen={isOpenAddAddressModal === addressFieldIndex}
        onClose={handleCloseProfileAddressModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <ProfileAddress
          addresses={addresses}
          fieldIndex={addressFieldIndex}
          onUpdateSuccess={handleAddAddressSuccess}
        />
      </Modal>
    </>
  );
};

const FieldDeliveryAddress = props => {
  return <Field component={FieldDeliveryAddressComponent} {...props} />;
};

export default withViewport(FieldDeliveryAddress);
